import { withNamespaces } from "react-i18next"
import Accordion from 'react-bootstrap/Accordion';
import { Card, CardBody, Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import DateUtils from "src/services/utils/DateUtils";
import { RiGroup2Line, RiInformationLine, RiMoneyDollarCircleLine, RiUser3Line, RiUserSettingsLine } from "react-icons/ri";
import { buildNestedObject } from "../../helpers";

const Ownership = ({
    t,
    activeAccordion,
    setActiveAccordion,

    detailsQuery
}) => {
    if (detailsQuery.isFetching) {
        return (
            <Row>
                <Col 
                    sm="12" 
                    className="mt-4 mb-4"
                >
                    <div 
                        className="dt-field dt-skeleton" 
                        style={{ width: '100%', height: '100px' }}
                    />
                </Col>
            </Row>
        )
    }

    const organizationData = JSON.parse(detailsQuery.data?.company?.organizationData || "");

    const generic = buildNestedObject(organizationData?.additional_information?.generic || []);
    
    const shareCapitalStructure = organizationData?.share_capital_structure || {};

    const additionalAuditorInformation = generic?.additionalAuditorInformation || [];

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('management-ownership')}
            className={activeAccordion === 'management-ownership' ? 'active' : ''}
            eventKey={'management-ownership'}
        >
            <Accordion.Header>
                <RiUser3Line size="24" className="me-2" />
                <span>
                    {t('Management & Ownership')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiMoneyDollarCircleLine size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Share Capital')}
                            </div>
                        </div>

                        <div className="mb-2">
                            <strong>
                                {t('Nominal Share Capital')}{' '}
                                <span
                                    id={`tooltip-share-capital-structure-nominal`}
                                    style={{ cursor: "pointer" }}>
                                    <RiInformationLine />
                                </span>
                                <UncontrolledTooltip
                                    target={`tooltip-share-capital-structure-nominal`}
                                >
                                    {t('The maximum amount of share capital the company is authorised by its constitutional documents to issue to shareholders.')}
                                </UncontrolledTooltip>{': '}
                            </strong>

                            {Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: 'EUR',
                            }).format(shareCapitalStructure.nominal_share_capital)}
                        </div>
                    </CardBody>
                </Card>

                {/* Shareholders */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiGroup2Line size="24" />
                            </span>

                            <div className="fw-bold font-size-16">
                                {t('Shareholders')}
                            </div>
                        </div>
                        {!shareCapitalStructure.share_holders?.length ? (
                            <div>{t('No shareholders data available')}</div>
                        ) : (
                            <Table striped bordered>
                                <thead>
                                    <tr>
                                        <th>{t('Name')}</th>
                                        <th>{t('Share %')}</th>
                                        <th>{t('Address')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shareCapitalStructure.share_holders.map((sh, i) => (
                                        <tr key={i}>
                                            <td>{sh.name}</td>
                                            <td>{sh.share_percent}</td>
                                            <td>{sh.address}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </CardBody>
                </Card>

                {/* Optional: Directors / Key Management (reuse auditors if needed) */}
                {additionalAuditorInformation && additionalAuditorInformation.length > 0 && (
                    <Card className="h-100 h-100 shadow-sm mb-4">
                        <CardBody className="p-1">
                            <div className="d-flex align-items-center mb-2">
                                <span className="me-2 text-primary">
                                    <RiUserSettingsLine size="24" />
                                </span>

                                <div className="fw-bold font-size-16">
                                    {t('Key Management / Auditors')}
                                </div>
                            </div>

                            <Table striped bordered>
                                <thead>
                                    <tr>
                                        <th>{t('Name')}</th>
                                        <th>{t('Gender')}</th>
                                        <th>{t('Director Type')}</th>
                                        <th>{t('Positions')}</th>
                                        <th>{t('Auditor Type')}</th>
                                        <th>{t('Corporation Type')}</th>
                                        <th>{t('PNR')}</th>
                                        <th>{t('SK')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {additionalAuditorInformation.map((person, i) => (
                                        <tr key={i}>
                                            <td>{person.name}</td>
                                            <td>{person.gender}</td>
                                            <td>{person.directorType}</td>
                                            <td>{person.positions.map(p => p.positionName).join(', ')}</td>
                                            <td>{person.auditorType}</td>
                                            <td>{person.type}</td>
                                            <td>{person.additionalData?.pnr}</td>
                                            <td>{person.additionalData?.sk}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                )}
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    Ownership
)